import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NotificationContext } from "../../contexts/NotificationContext";
import { CheckoutComponentNotificationType } from "../../types/builder";
import Toast from "../common/Toast";
import type { Notification } from "@/hooks/useNotifications";

const Icon: Record<CheckoutComponentNotificationType, string> = {
  [CheckoutComponentNotificationType.INTERESTED_LAST_24_HOURS]: "/assets/group.png",
  [CheckoutComponentNotificationType.INTERESTED_LAST_WEEK]: "/assets/growth.png",
  [CheckoutComponentNotificationType.INTERESTED_RIGHT_NOW]: "/assets/growth.png",
  [CheckoutComponentNotificationType.PURCHASED_LAST_24_HOURS]: "/assets/check.png",
  [CheckoutComponentNotificationType.PURCHASED_LAST_WEEK]: "/assets/check.png",
};

/**
 * Único consumidor que renderiza notificações no portal toast-root.
 * Garante exibição sequencial (uma por vez) e evita sobreposição/duplicata
 * quando há múltiplos CheckoutConfigRenderer na árvore.
 */
const NotificationToasts = () => {
  const { notifications } = useContext(NotificationContext);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [queue, setQueue] = useState<Notification[]>([]);
  const initialQueueSetRef = useRef(false);

  const dismissCurrent = () => {
    setCurrentNotification(null);
  };

  // Só preenche a fila uma vez quando chegam notificações; reseta quando a lista é limpa (ex.: troca de oferta)
  useEffect(() => {
    if (notifications.length === 0) {
      initialQueueSetRef.current = false;
      return;
    }
    if (initialQueueSetRef.current) return;
    initialQueueSetRef.current = true;
    setQueue(notifications);
  }, [notifications]);

  // Consome a fila sequencialmente: quando não há notificação visível, mostra a próxima
  useEffect(() => {
    if (currentNotification != null || queue.length === 0) return;
    const [next, ...remaining] = queue;
    setCurrentNotification(next);
    setQueue(remaining);
  }, [currentNotification, queue]);

  const toastRoot = document.getElementById("toast-root");
  if (!toastRoot) return null;

  return createPortal(
    <div className="fixed top-0 right-0 z-[9999] p-5 flex flex-col gap-3 pointer-events-none">
      <div className="flex flex-col gap-3 pointer-events-auto">
        {currentNotification && (
          <Toast
            key={currentNotification.id}
            exibitionTime={currentNotification.exibitionTime}
            onClose={dismissCurrent}
          >
            <div className="flex flex-row items-center gap-4 overflow-hidden">
              <img
                className="w-14 h-14 rounded-full shrink-0"
                src={Icon[currentNotification.type]}
                alt={currentNotification.type}
              />
              <span className="text-sm text-gray-700 break-words max-w-full">
                {currentNotification.message}
              </span>
            </div>
          </Toast>
        )}
      </div>
    </div>,
    toastRoot
  );
};

export default NotificationToasts;
